import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { useSystemFlags } from "@/contexts/SystemFlagsContext";
import ProviderCard from "@/components/ProviderCard";
import FilterPanel from "@/components/FilterPanel";
import MapPreview from "@/components/MapPreview";
import BoredNowButton from "@/components/BoredNowButton";
import InstantRequestFeed from "@/components/InstantRequestFeed";
import GuestBanner from "@/components/GuestBanner";
import OffersBanner from "@/components/OffersBanner";
import LocationPromptBanner from "@/components/LocationPromptBanner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, MapPin, Heart, Users, CalendarDays, Map as MapIcon, Sparkles, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const DEFAULT_FILTER = {
  minPrice: 20,
  maxPrice: 200,
  minAge: 18,
  maxAge: 65,
  minRating: 0,
  availableNow: false,
  radiusKm: 25,
};

const CITY_PRESETS = [
  ["San Francisco", 37.7749, -122.4194],
  ["New York", 40.7128, -74.006],
  ["London", 51.5074, -0.1278],
  ["Tokyo", 35.6762, 139.6503],
  ["Berlin", 52.52, 13.405],
  ["Mumbai", 19.076, 72.8777],
  ["Paris", 48.8566, 2.3522],
  ["Lisbon", 38.7223, -9.1393],
  ["Sydney", -33.8688, 151.2093],
  ["Dubai", 25.2048, 55.2708],
];

export default function Discovery() {
  const { flags } = useSystemFlags();
  const hangoutsEnabled = flags?.hangouts_enabled !== false;
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [filters, setFilters] = useState(DEFAULT_FILTER);
  const [sort, setSort] = useState("smart");
  const [coords, setCoords] = useState(null);
  const [viewMode, setViewMode] = useState("grid"); // grid | map
  const [city, setCity] = useState("");
  const [whenDate, setWhenDate] = useState(undefined);
  const [duration, setDuration] = useState(2);
  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const queryParams = useMemo(() => {
    const p = { limit: 12, sort };
    if (debouncedQ) p.q = debouncedQ;
    if (filters.minPrice > 20) p.min_price = filters.minPrice;
    if (filters.maxPrice < 200) p.max_price = filters.maxPrice;
    if (filters.minAge > 18) p.min_age = filters.minAge;
    if (filters.maxAge < 65) p.max_age = filters.maxAge;
    if (filters.minRating > 0) p.min_rating = filters.minRating;
    if (filters.availableNow) p.available_now = true;
    if (whenDate) p.date = whenDate.toISOString().slice(0, 10);
    if (coords) {
      p.lat = coords.lat;
      p.lng = coords.lng;
      p.radius_km = filters.radiusKm;
    }
    return p;
  }, [debouncedQ, filters, sort, coords, whenDate]);

  // Reset to first page when params change (but keep current items visible
  // until the new ones arrive — avoids a "blink" of empty results).
  useEffect(() => {
    setSkip(0);
    setHasMore(true);
  }, [queryParams]);

  const fetchPage = useCallback(
    async (currentSkip) => {
      setLoading(true);
      try {
        const { data } = await api.get("/providers", {
          params: { ...queryParams, skip: currentSkip },
        });
        setItems((prev) => (currentSkip === 0 ? data.items : [...prev, ...data.items]));
        setHasMore(data.has_more);
      } finally {
        setLoading(false);
      }
    },
    [queryParams]
  );

  useEffect(() => {
    fetchPage(0);
  }, [fetchPage]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && hasMore) {
          const next = items.length;
          setSkip(next);
          fetchPage(next);
        }
      },
      { rootMargin: "300px" }
    );
    observerRef.current.observe(sentinelRef.current);
    return () => observerRef.current?.disconnect();
  }, [items, loading, hasMore, fetchPage]);

  const requestMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setCity("Near me");
      },
      () => {}
    );
  };

  const whenLabel = whenDate
    ? whenDate.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })
    : "Any date";

  // ---------- JSON-LD structured data for Google rich cards ----------
  const jsonLdBlobs = useMemo(() => {
    const ORIGIN = "https://companin.com";
    const blobs = [];

    // 1) Service schema for the COMPANIN platform itself
    const service = {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "COMPANIN — Verified companions, by the hour",
      url: `${ORIGIN}/discovery`,
      description:
        "Browse verified, kind companions on COMPANIN — book real people by the hour for a coffee, a walk, or a long conversation.",
      provider: {
        "@type": "Organization",
        name: "COMPANIN",
        url: ORIGIN,
        logo: `${ORIGIN}/logo512.png`,
      },
      areaServed: ["United Kingdom", "Worldwide"],
      serviceType: "Companion booking",
    };

    // 2) AggregateRating across rated providers only — Google rejects fake/empty ratings
    const ratedProviders = items.filter(
      (p) => typeof p.rating === "number" && p.rating > 0 && (p.review_count || 0) > 0,
    );
    if (ratedProviders.length > 0) {
      const totalReviews = ratedProviders.reduce((s, p) => s + (p.review_count || 0), 0);
      const weighted =
        ratedProviders.reduce((s, p) => s + p.rating * (p.review_count || 0), 0) / totalReviews;
      service.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: weighted.toFixed(2),
        reviewCount: totalReviews,
        bestRating: 5,
        worstRating: 1,
      };
    }
    blobs.push(service);

    // 3) ItemList of currently-visible providers → Google rich list / carousel
    if (items.length > 0) {
      blobs.push({
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListOrder: "https://schema.org/ItemListOrderDescending",
        numberOfItems: items.length,
        itemListElement: items.slice(0, 12).map((p, idx) => ({
          "@type": "ListItem",
          position: idx + 1,
          url: `${ORIGIN}/providers/${p.provider_id}`,
          item: {
            "@type": "Person",
            name: p.name,
            url: `${ORIGIN}/providers/${p.provider_id}`,
            image: (p.images && p.images[0]) || undefined,
            address: p.city
              ? { "@type": "PostalAddress", addressLocality: p.city }
              : undefined,
            description: p.short_bio || p.bio || undefined,
          },
        })),
      });
    }

    return blobs;
  }, [items]);

  return (
    <div data-testid="discovery-page">
      <SEO
        title="Discover verified companions"
        description="Browse verified companions on COMPANIN — kind, real people available for coffee, walks, deep conversations. Book by the hour, judgment-free."
        canonical="https://companin.com/discovery"
        jsonLd={jsonLdBlobs}
      />
      {/* Hero with Airbnb-style search */}
      <section className="relative pt-10 sm:pt-16 pb-10 bg-gradient-to-b from-[#F3EDE4] via-[#FAF6EF] to-[#FAFAF9] border-b border-stone-200">
        {/* Floating "I'm Bored Now" instant-match CTA */}
        <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 hidden sm:block">
          <BoredNowButton />
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <p className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#C05640] font-bold">
            <Heart className="h-3 w-3 fill-[#C05640]" /> No one should feel alone
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium text-stone-900 mt-3 leading-tight">
            Find someone to spend time with.
          </h1>
          <p className="text-stone-600 mt-3 max-w-xl mx-auto text-base sm:text-lg">
            Verified, kind companions for a coffee, a walk, a long talk — book by the hour, judgment-free.
          </p>

          {/* Airbnb-style search pill — rounded card on mobile, pill on desktop */}
          <div
            className="mt-8 bg-white border border-stone-200 rounded-3xl md:rounded-full shadow-md hover:shadow-lg transition-shadow p-2 md:p-1.5 flex flex-col md:flex-row md:items-stretch max-w-3xl mx-auto divide-y divide-stone-200 md:divide-y-0"
            data-testid="hero-search-bar"
          >
            {/* Where */}
            <Select
              value={city}
              onValueChange={(v) => {
                if (v === "__near__") {
                  requestMyLocation();
                } else if (v === "__any__") {
                  setCity("");
                  setCoords(null);
                } else {
                  const found = CITY_PRESETS.find((c) => c[0] === v);
                  if (found) {
                    setCity(found[0]);
                    setCoords({ lat: found[1], lng: found[2] });
                  }
                }
              }}
            >
              <SelectTrigger
                className="h-14 flex-1 rounded-2xl md:rounded-full hover:bg-stone-50 border-0 shadow-none text-left px-5 md:px-6 focus:ring-0 [&>span]:text-left"
                data-testid="hero-city-select"
              >
                <div className="flex items-center gap-3 w-full">
                  <MapPin className="h-4 w-4 text-[#4A5D4E] shrink-0" />
                  <div className="flex flex-col items-start">
                    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-stone-500">Where</span>
                    <SelectValue placeholder="Any location" />
                  </div>
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">Any location</SelectItem>
                <SelectItem value="__near__">Use my location</SelectItem>
                {CITY_PRESETS.map(([name]) => (
                  <SelectItem key={name} value={name}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="hidden md:block w-px bg-stone-200 my-3" />

            {/* When */}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="h-14 flex-1 rounded-2xl md:rounded-full hover:bg-stone-50 text-left px-5 md:px-6 flex items-center gap-3"
                  data-testid="hero-when-button"
                >
                  <CalendarDays className="h-4 w-4 text-[#4A5D4E] shrink-0" />
                  <div className="flex flex-col items-start">
                    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-stone-500">When</span>
                    <span className="text-sm text-stone-900">{whenLabel}</span>
                  </div>
                </button>
              </PopoverTrigger>
              <PopoverContent align="center" className="w-auto p-0 rounded-2xl" data-testid="hero-when-popover">
                <Calendar
                  mode="single"
                  selected={whenDate}
                  onSelect={setWhenDate}
                  fromDate={new Date()}
                  initialFocus
                />
                {whenDate && (
                  <div className="px-3 pb-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setWhenDate(undefined)}
                      className="w-full rounded-full text-stone-600"
                    >
                      Clear date
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>

            <div className="hidden md:block w-px bg-stone-200 my-3" />

            {/* Duration */}
            <Select value={String(duration)} onValueChange={(v) => setDuration(+v)}>
              <SelectTrigger
                className="h-14 flex-1 rounded-2xl md:rounded-full hover:bg-stone-50 border-0 shadow-none text-left px-5 md:px-6 focus:ring-0"
                data-testid="hero-duration-select"
              >
                <div className="flex items-center gap-3 w-full">
                  <Users className="h-4 w-4 text-[#4A5D4E] shrink-0" />
                  <div className="flex flex-col items-start">
                    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-stone-500">How long</span>
                    <SelectValue />
                  </div>
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 hour</SelectItem>
                <SelectItem value="2">2 hours</SelectItem>
                <SelectItem value="3">3 hours</SelectItem>
                <SelectItem value="4">4 hours</SelectItem>
                <SelectItem value="6">6 hours</SelectItem>
                <SelectItem value="8">A full day</SelectItem>
              </SelectContent>
            </Select>

            <Button
              size="lg"
              className="h-12 md:h-11 mt-2 md:mt-1 md:ml-2 md:mr-1 rounded-2xl md:rounded-full bg-[#C05640] hover:bg-[#A64A37] text-white px-6 font-medium shadow-sm w-full md:w-auto"
              data-testid="hero-search-button"
              onClick={() => {
                // Scroll to results
                document.getElementById("results")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            >
              <Search className="h-4 w-4 mr-2" /> Search
            </Button>
          </div>

          {/* Trust line */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm text-stone-600">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> KYC-verified companions
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-sky-500" /> Encrypted chat &amp; payments
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> 24/7 trust &amp; safety
            </span>
          </div>

          {/* Sample-profile disclaimer removed — demo providers are now indistinguishable from real ones at the UI layer. Backend silently 409s any booking attempt against them. */}
        </div>
      </section>

      {/* Results */}
      <section id="results" className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Live limited-time offers (admin-managed) */}
        <OffersBanner audience="customer" />

        {/* Soft prompt to share location on first visit */}
        <LocationPromptBanner
          hasCoords={!!coords}
          onLocationGranted={({ lat, lng }) => {
            setCoords({ lat, lng });
            // Smart default — if user just granted, switch to closest-first.
            if (sort === "smart") setSort("distance");
          }}
        />

        {/* Guest browsing banner */}
        <GuestBanner />

        {/* Mobile-only Bored Now CTA (desktop has the floating one) */}
        <div className="sm:hidden mb-6 flex justify-center">
          <BoredNowButton />
        </div>

        {/* Provider-only: live instant requests feed */}
        <InstantRequestFeed />

        {/* Map preview (only when a city/coords selected) */}
        {coords && items.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <MapIcon className="h-4 w-4 text-stone-500" />
              <span className="text-xs uppercase tracking-[0.16em] font-bold text-stone-500">
                Map preview {city && `· ${city}`}
              </span>
            </div>
            <MapPreview center={coords} providers={items} height={320} />
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-medium text-stone-900">
              {items.length > 0 ? `${items.length}${hasMore ? "+" : ""} companions` : "Companions"}
              {city && <span className="text-stone-500 font-normal"> in {city}</span>}
            </h2>
            <p className="text-sm text-stone-500 mt-0.5">
              Real people, real conversations — book by the hour.
            </p>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap sm:flex-nowrap">
            <div className="relative w-full sm:w-64 order-1">
              <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Refine by interest…"
                className="h-11 pl-11 pr-4 rounded-full bg-white border-stone-300 w-full shadow-sm focus-visible:ring-2 focus-visible:ring-[#4A5D4E]/30"
                data-testid="discovery-search-input"
              />
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="h-11 rounded-full border-stone-300 bg-white px-4 flex-1 sm:flex-initial sm:min-w-[150px] shadow-sm order-2" data-testid="sort-select">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="smart">Smart picks</SelectItem>
                <SelectItem value="rating">Top rated</SelectItem>
                <SelectItem value="price_asc">Price: low to high</SelectItem>
                <SelectItem value="price_desc">Price: high to low</SelectItem>
                {coords && <SelectItem value="distance">Closest first</SelectItem>}
              </SelectContent>
            </Select>
            <div className="order-3 flex items-center gap-2">
              <FilterPanel value={filters} onChange={setFilters} />
              <div
                className="inline-flex bg-white border border-stone-300 rounded-full p-1 shadow-sm shrink-0"
                data-testid="view-mode-toggle"
              >
                <button
                  onClick={() => setViewMode("grid")}
                  className={`h-9 px-3 rounded-full text-xs font-semibold transition-colors ${
                    viewMode === "grid" ? "bg-[#4A5D4E] text-white" : "text-stone-600 hover:text-stone-900"
                  }`}
                  data-testid="view-mode-grid"
                >
                  Grid
                </button>
                <button
                  onClick={() => setViewMode("map")}
                  className={`h-9 px-3 rounded-full text-xs font-semibold transition-colors ${
                    viewMode === "map" ? "bg-[#4A5D4E] text-white" : "text-stone-600 hover:text-stone-900"
                  }`}
                  data-testid="view-mode-map"
                >
                  Map
                </button>
              </div>
            </div>
          </div>
        </div>

        {items.length === 0 && !loading ? (
          <div className="bg-white border border-stone-200 rounded-2xl p-10 text-center" data-testid="no-results">
            <p className="font-display text-xl text-stone-900">No matches.</p>
            <p className="text-stone-600 mt-1">Try widening the radius, adjusting price, or clearing filters.</p>
            <Button
              onClick={() => {
                setFilters(DEFAULT_FILTER);
                setQ("");
              }}
              className="mt-4 rounded-full bg-[#4A5D4E] hover:bg-[#3B4A3E] text-white"
            >
              Reset filters
            </Button>
          </div>
        ) : viewMode === "map" ? (
          <div data-testid="provider-map-view">
            <MapPreview
              center={coords || (items[0] && { lat: items[0].lat, lng: items[0].lng })}
              providers={items}
              height={560}
            />
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 stagger"
            data-testid="provider-grid"
          >
            {items.map((p) => (
              <ProviderCard key={p.provider_id} p={p} />
            ))}
          </div>
        )}

        {loading && (
          <div className="text-center py-8 text-stone-500" data-testid="loading-more">
            Loading more…
          </div>
        )}
        <div ref={sentinelRef} className="h-2" />

        {/* Group hangouts teaser — only when admin has enabled the flag */}
        {hangoutsEnabled && (
        <div className="mt-12 rounded-2xl bg-gradient-to-br from-[#4A5D4E] to-[#5C7361] text-white p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 shadow-md" data-testid="discovery-hangouts-teaser">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-2xl bg-white/15 grid place-items-center shrink-0">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-white/70 font-bold">Hangouts</p>
              <h3 className="font-display text-2xl sm:text-3xl font-medium mt-1">
                Don&apos;t want a 1:1? Drop into a group hangout.
              </h3>
              <p className="text-white/80 mt-1 text-sm sm:text-base max-w-xl">
                Up to 4 guests, hosted by verified companions. Shared cost, shared laughs.
              </p>
            </div>
          </div>
          <Link to="/group-sessions">
            <Button
              size="lg"
              className="rounded-full bg-white text-stone-900 hover:bg-stone-100 px-6 shrink-0"
              data-testid="discovery-hangouts-cta"
            >
              Browse hangouts <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
        )}
      </section>
    </div>
  );
}
